import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Third Party
import moment from 'moment';

// Styles
import styles from './jobListing.module.scss';

// Helpers
import { formatMoney } from 'helpers/HelperFunctions';
import { removeMarkdownSyntax } from '../../helpers/HelperFunctions';
import {
  FREE_USER_JOB_PITCH_LIMIT,
} from 'constants/constants';
import { saveJob, unsaveJob } from '../../redux/modules/projects2';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';

import SvgIcon from 'components/SvgIcon/SvgIcon';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

function Chip({ text, onClick, relatedJobs }) {
  return (
    <div
      style={{ cursor: relatedJobs ? 'default' : 'pointer' }}
      className={styles.jobCard__chips}
      onClick={onClick}
    >
      {text}
    </div>
  );
}

export default function JobListing({
  brief,
  onClickRole,
  onClickLocation,
  relatedJobs,
  onUnsave,
  onShowSignUpModal,
}) {
  const {
    id,
    text,
    external,
    expires_at,
    approved_at,
    num_of_pitches,
    location,
    // skill, Commenting out until skills are implemented
    budget,
    currency,
    boosted_expires_at,
    saved_by_current_user,
  } = brief;

  const [isSaved, setIsSaved] = useState(saved_by_current_user || false);
  const [isRemoving, setIsRemoving] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  let metaDisplayname = null;

  if (brief.user?.displayname) {
    metaDisplayname = brief.user.displayname.match(
      /Twine Jobs|user[0-9]+|^client$|^private$/i
    )
      ? null
      : brief.user.displayname;
  }

  let country = '';
  let city = '';

  const roleChips = brief.roles.map((role) => {
    return (
      <Chip
        relatedJobs={relatedJobs}
        key={role + '1'}
        text={role}
        onClick={relatedJobs ? undefined : (e) => handleRoleChipClick(e, role)}
      />
    );
  });

  const handleRoleChipClick = (e, role) => {
    e.stopPropagation();
    onClickRole({ name: role });
  };

  const handleLocationChipClick = (e, city, country) => {
    e.stopPropagation();
    onClickLocation({ city: city, country: country });
  };

  const locationChip = (briefLocation) => {
    if (briefLocation) {
      if (briefLocation.includes(',')) {
        [city, country] = briefLocation.split(', ');
      } else {
        country = briefLocation;
      }

      return (
        <Chip
          relatedJobs={relatedJobs}
          text={'📍 ' + briefLocation}
          onClick={
            relatedJobs
              ? undefined
              : (e) => handleLocationChipClick(e, city, country)
          }
        />
      );
    }
    return <Chip relatedJobs={relatedJobs} text={'🌎 Remote'} onClick={null} />;
  };

  const budgetChip = (briefBudget, briefCurrency) => {
    if (briefBudget < 0) {
      return (
        <Chip relatedJobs={relatedJobs} text="💰 Negotiable" onClick={null} />
      );
    }
    const price = formatMoney(briefBudget, briefCurrency, true);
    return (
      <Chip relatedJobs={relatedJobs} text={'💰 ' + price} onClick={null} />
    );
  };

  const briefIsExpired = (expiry) => {
    if (moment().isAfter(moment(expiry))) {
      return true;
    }
    return false;
  };

  const openOrPopularJob = (pitchAmount, mobile) => {
    let cssString = `${styles.jobCard__status} ${styles.desktop}`;
    if (mobile) {
      cssString = `${styles.jobCard__status} ${styles.mobile}`;
    }

    if (boosted_expires_at) {
      return <div className={`${cssString} ${styles.priority}`}>PRIORITY</div>;
    }

    if (pitchAmount >= FREE_USER_JOB_PITCH_LIMIT) {
      return <div className={`${cssString} ${styles.popular}`}>POPULAR</div>;
    }

    return <div className={`${cssString} ${styles.open}`}> OPEN JOB </div>;
  };

  const renderBriefSpec = (brief) => {
    const { spec } = brief;
    let newSpec = removeMarkdownSyntax(spec);

    return newSpec.substring(0, 300) + '...';
  };

  const handleBookmarkClick = () => {
    // Determine which page the component is on based on URL
    const currentUrl = window.location.pathname;
    let page = '';

    if (currentUrl === '/jobs') {
      page = 'jobs page';
    } else if (currentUrl === '/saved-jobs') {
      page = 'saved jobs page';
    } else if (currentUrl.match(/\/projects\/[^/]+/)) {
      page = 'related jobs section';
    }

    if (isSaved) {
      if (onUnsave) {
        setIsRemoving(true);
        setTimeout(() => {
          dispatch(unsaveJob(id));
          trackEvent('user-unsaved-job', {
            userId: user ? user.id : 'Guest',
            briefId: brief.id,
            page,
          });

          onUnsave(id);
        }, 500); // Match this with the CSS transition duration
      } else {
        dispatch(unsaveJob(id));
        trackEvent('user-unsaved-job', {
          userId: user ? user.id : 'Guest',
          briefId: brief.id,
          page,
        });
        setIsSaved(false);
      }
    } else {
      dispatch(saveJob(id));
      trackEvent('user-saved-job', {
        userId: user ? user.id : 'Guest',
        briefId: brief.id,
        page,
      });
      setIsSaved(true);
    }
  };

  const jobCardClasses = `${styles.jobCard} ${
    isRemoving ? styles.removing : ''
  }`;

  return (
    <div className={jobCardClasses} key={brief.id}>
      {brief.user?.avatars && metaDisplayname && (
        <Thumbnail
          thumbnails={brief.user.avatars}
          circle
          className={styles.jobCard__avatar}
        />
      )}
      <div className={styles.jobCard__description}>
        <div className={styles.jobCard__header}>
          <a
            className={styles.jobCard__name}
            href={brief.links.main_relative}
            target="_blank"
            rel="noreferrer"
          >
            <div className={styles.jobCard__text}>
              {text}
              {!external && (
                <span className={styles.jobCard__easyApply}>Easy Apply ⚡</span>
              )}
            </div>
            {!briefIsExpired(expires_at) && (
              <>{openOrPopularJob(num_of_pitches)}</>
            )}
          </a>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip
                id="tooltip"
                style={{ fontSize: '14px', fontFamily: 'Lato' }}
              >
                {isSaved ? 'Unsave Job' : 'Save Job'}
              </Tooltip>
            }
          >
            <div
              className={styles.jobCard__bookmark}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onShowSignUpModal && !user) {
                  onShowSignUpModal();
                } else {
                  handleBookmarkClick();
                }
              }}
            >
              <SvgIcon
                icon={isSaved ? 'bookmark' : 'bookmark-outline'}
                color="#777"
                className={styles.jobCard__bookmarkIcon}
              />
            </div>
          </OverlayTrigger>
        </div>
        {metaDisplayname && (
          <div className={styles.jobCard__client}>{metaDisplayname}</div>
        )}
        <div className={styles.jobCard__tags}>
          {locationChip(location)}
          {budgetChip(budget, currency)}
          {roleChips}
          {/* Commented out until Skills are added to jobs */}
          {/* {skill && <Chip text={skill} />} */}
        </div>
        <a
          className={styles.jobCard__spec}
          href={brief.links.main_relative}
          target="_blank"
          rel="noreferrer"
        >
          {renderBriefSpec(brief)}{' '}
          <span className={styles.jobCard__specLink}>Read more</span>
        </a>
        <div className={styles.jobCard__postedInfo}>
          Posted {moment(approved_at).fromNow()}
          {!briefIsExpired(expires_at) && (
            <>
              <span className={styles.jobCard__bullet}>&bull;</span>
              <div className={styles.jobCard__expiryInfo}>
                Expires in {moment(expires_at).fromNow(true)}
              </div>
              {openOrPopularJob(num_of_pitches, true)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

JobListing.propTypes = {
  brief: PropTypes.object.isRequired,
  onClickRole: PropTypes.func,
  onClickLocation: PropTypes.func,
  relatedJobs: PropTypes.bool,
  onUnsave: PropTypes.func,
  onShowSignUpModal: PropTypes.func,
};

Chip.propTypes = {
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  relatedJobs: PropTypes.bool,
};
